import { Component, computed, inject, Input, Signal } from '@angular/core';
import { CalendarIntegrationStore } from '@app/_stores';
import { CalendarEvent, CalendarEventSetup } from '@app/_interfaces/calendar-integration.interface';
import { Entry } from '@app/_models/entry.model';
import { getLocalDateString as getDateString } from '@app/_services/date';
import { resolveDateTime } from '@app/_utils/resolve-date-time.util';
import { uses24HourTimeForLanguage } from '@app/_generated/locale-config.gen';
import { CommonModule } from '@angular/common';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MatDialog } from '@angular/material/dialog';
import { CalendarEventFormComponent } from '../calendar-event-form/calendar-event-form.component';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { ParseMinutesPipe } from '@app/_pipes/parse-minutes.pipe';
import { SettingsService } from '@app/_services/settings.service';
import { CalendarIntegrationService } from '@app/_services/features/calendar-integration.service';
import { ModalService } from '@app/_services/modal.service';
import { descriptionToHtml } from '@app/_utils/text-format.utils';

@Component({
  selector: 'app-calendar-event-block',
  imports: [
    CommonModule,
    SvgComponent,
    ParseMinutesPipe,
  ],
  templateUrl: './calendar-event-block.component.html',
  styleUrl: './calendar-event-block.component.scss'
})

export class CalendarEventBlockComponent {

  readonly calendarStore = inject(CalendarIntegrationStore);
  @Input() show: string[] = [];
  @Input() blockClass: string = '';
  @Input() dateString: string = getDateString();
  @Input() entry!: Entry;
  @Input() descriptionType: string = 'none';
  @Input() isLabel: boolean = true;

  eventSetup: Signal<CalendarEventSetup> = computed(() => {
    return this.calendarStore.idToEventSetup()[this.entry.setupId];
  })

  /** Memoized description HTML — only recomputes when event data changes. */
  descriptionHtml: Signal<string> = computed(() => {
    const eventData = this.event();
    if (!eventData?.description) return '';
    const html = descriptionToHtml(eventData.description);
    if (this.descriptionType === 'short') {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      if (textContent.length > 150) {
        return '<p>' + textContent.substring(0, 150) + '...</p>';
      }
    }
    return html;
  });

  event: Signal<CalendarEvent | null> = computed(() => {
    const setup = this.eventSetup();
    if (!setup) return null;
    const dateString = this.dateString;
    const calendarIntegrationId = setup.calendarIntegrationId;
    const eventMap = this.calendarStore.eventMap();
    const eventDataFromMap: CalendarEvent | null = eventMap[calendarIntegrationId]?.[setup.id]?.[dateString] || null;
    // const constructedEvent = this.setupToCalendarEvents(setup);
    const eventData = eventDataFromMap ? { ...(eventDataFromMap as CalendarEvent), repeat: setup.repeat } : this.calendarService.setupToCalendarEvents(setup);
    return eventData;
  });

  /** Resolved local time for display — converts from event's timezone to device local. */
  resolvedLocalTime: Signal<Date | null> = computed(() => {
    const evt = this.event();
    if (!evt?.startTime || evt.startTime === '00:00') return null;
    const setup = this.eventSetup();
    return resolveDateTime(
      evt.startDate ?? setup?.startDate,
      evt.startTime,
      evt.timeZoneId ?? setup?.timeZoneId,
    );
  });

  /** Formatted resolved time string for template display. */
  resolvedTimeStr: Signal<string> = computed(() => {
    const date = this.resolvedLocalTime();
    if (!date) return this.ss.t('common_noTime');
    const hours = date.getHours();
    const min = date.getMinutes().toString().padStart(2, '0');
    if (uses24HourTimeForLanguage(this.ss.appLanguageCode())) {
      return `${hours.toString().padStart(2, '0')}:${min}`;
    }
    const period = hours >= 12
      ? this.ss.t('timePicker_pm')
      : this.ss.t('timePicker_am');
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${min} ${period}`;
  });

  constructor(public dialog: MatDialog, public cc: CacheService, public ds: DependencyService, public ss: SettingsService, private calendarService: CalendarIntegrationService, private modalService: ModalService) {

  }

  openCalendarEventForm() {
    this.modalService.openLevel1(
      CalendarEventFormComponent,
      {
        mode: 'edit',
        value: this.eventSetup(),
        dateString: this.dateString
      },
      { disableClose: true, autoFocus: false }
    );
  }
}
